import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Switch,
  Alert,
  Popover,
  useMediaQuery,
} from "@mui/material";
import { Add, Delete, ExpandMore } from "@mui/icons-material";
import CustomDatePicker from "../customDatePicker/customDatePicker.component";
import ContractEndDatePicker from "../contractEndDatePicker/contractEndDatePicker.component";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs, { Dayjs } from "dayjs";
import axios from "../../../api/axios";
import { useApiRequest } from "../../../api/axios";
import { useCRUD } from "../../../api/crud";
import {
  CLIENTS_ENDPOINT,
  CONTRACTS_ENDPOINT,
  CONTRACTS_SAVE_WITH_TERMS_ENDPOINT,
  CONTRACT_TERMS_ENDPOINT,
  NUMERATION_SCHEMA_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from "../../../consts";
import AnimatedHelperText from "../animatedHelperText/animatedHelperText.component";
import CustomCircularProgress from "../customCircularProgress/customCircularProgress.component";
import EquipmentAutocomplete from "../equipmentAutocomplete/equipmentAutocomplete.component";
import EquipmentCounterForm, {
  PreviousCountersTable,
  useEquipmentLastCounters,
  type LastCounterData,
} from "../equipmentCounterForm/equipmentCounterForm.component";
import CustomAutocomplete from "../customAutocomplete/customAutocomplete.component";
import AddressForm, { defaultAddressFormData } from "../addressForm/addressForm.component";
import { DECIMAL_TWO_PLACES_INPUT_REGEX, DECIMAL_FOUR_PLACES_INPUT_REGEX, formatCounterValue, ContractEndDateDisplay, formatNumber } from "../../../utils";
import { COMMON_COUNTER_KIND } from "../../../utils/settlementContractTerms";

const DEFAULT_SETTLEMENT_CYCLE = "M";
const DEFAULT_VAT_RATE = 23;
const PER_USAGE_SETTLEMENT_KIND = "U";

type PriceInputMode = "netto" | "brutto";

export interface OpeningHourRow {
  id?: string;
  week_day: string;
  from_hour: string;
  to_hour: string;
}

// Input fields only. Deliberately no thousands separators: grouping inside an
// editable field fights the caret on every keystroke. Read-only amounts go
// through formatNumber instead, which groups them the way the rest of the app
// renders money.
function formatPriceForDisplay(value: string): string {
  if (!value) return "";
  return String(value).replace(/\./g, ",");
}

function parsePriceInput(value: string): number {
  if (!value) return NaN;
  return parseFloat(value.replace(",", "."));
}

// Prices support up to 4 decimal places, so keep that precision when converting
// between net and gross instead of collapsing to grosze mid-edit. Trailing
// zeros past the 2nd place are dropped so a plain "200" rent doesn't render as
// "200,0000".
function roundPrice(value: number): string {
  return String(Number(value.toFixed(4)));
}

// Prices come back from the API padded to 4 decimals ("100.0000"); show them in
// the form without the padding so a plain rent doesn't read as "100,0000".
function normalizeStoredPrice(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? roundPrice(parsed) : String(value);
}

function bruttoToNetto(brutto: number, vatRate: number = DEFAULT_VAT_RATE): string {
  return roundPrice(brutto / (1 + vatRate / 100));
}

function nettoToBrutto(netto: number, vatRate: number = DEFAULT_VAT_RATE): string {
  return roundPrice(netto * (1 + vatRate / 100));
}

function mapAddressToFormAddress(address: any) {
  if (!address) return null;

  const toTime = (value: string) => (value ? String(value).slice(0, 5) : "");
  const rawOpeningHours = Array.isArray(address.opening_hours) ? address.opening_hours : [];

  const openingHours: OpeningHourRow[] = rawOpeningHours.map((oh: any) => ({
    week_day: oh.week_day != null ? String(oh.week_day) : "",
    from_hour: toTime(oh.from_hour ?? ""),
    to_hour: toTime(oh.to_hour ?? ""),
  }));

  return {
    ...defaultAddressFormData,
    country: address.country || defaultAddressFormData.country,
    city: address.city || "",
    street: address.street || "",
    zip_code: address.zip_code || "",
    phone_number: address.phone_number || "",
    email: address.email || "",
    email_counter_reminders: address.email_counter_reminders || "",
    notes: address.notes || "",
    opening_hours: openingHours,
  };
}

interface ContractFormData {
  client_id: string | null;
  numeration_schema_id: string | null;
  number: string;
  internal_name: string;
  start_date: string;
  end_date: string;
  indefinite_after_end_date: boolean;
  issuance_date: string;
  signature_date: string;
  inflation_condition: string;
  inflation_rate_percent: string;
  is_brutto: boolean;
}

interface ContractTermFormData {
  id?: string;
  active: boolean;
  equipment_ids?: string[];
  equipments?: EquipmentOption[];
  kind: string;
  settlement_cycle: string;
  next_settlement_date: string;
  rent_price: string;
  rent_price_input_mode?: PriceInputMode;
  bill_usage_from_start: boolean;
  installation_address: {
    country: string;
    city: string;
    street: string;
    zip_code: string;
    phone_number: string;
    email: string;
    email_counter_reminders: string;
    notes: string;
    opening_hours?: OpeningHourRow[];
  } | null;
  prices: ContractTermPriceFormData[];
  counters: CounterDraft[];
  end_counters: CounterDraft[];
}

interface EquipmentOption {
  id: string;
  full_name?: string;
  name?: string;
  kind?: string;
  installation_address?: {
    street?: string;
    city?: string;
    zip_code?: string;
    country?: string;
  };
  [key: string]: unknown;
}

const EQUIPMENT_KIND_BLACK = "B";
const EQUIPMENT_KIND_COLOR = "C";

function getPersistedTermKey(term: ContractTermFormData, termIndex: number): string {
  return term.id || (isCommonCounterTerm(term) ? `ccr-${termIndex}` : `term-${termIndex}`);
}

function createEmptyContractTerm(): ContractTermFormData {
  return {
    active: true,
    equipments: [],
    kind: "",
    settlement_cycle: DEFAULT_SETTLEMENT_CYCLE,
    next_settlement_date: "",
    rent_price: "",
    rent_price_input_mode: "netto",
    bill_usage_from_start: false,
    installation_address: null,
    prices: [],
    counters: [],
    end_counters: [],
  };
}

function extractErrorMessages(data: unknown): string[] {
  if (typeof data === "string") return data ? [data] : [];
  if (Array.isArray(data)) {
    return data.flatMap((item) => {
      if (item && typeof item === "object" && "message" in item) return [String((item as any).message)];
      return extractErrorMessages(item);
    }).filter(Boolean);
  }
  if (data && typeof data === "object") {
    if ("message" in data) return [String((data as any).message)];
    return Object.values(data).flatMap(extractErrorMessages).filter(Boolean);
  }
  return [];
}

function CounterDefaultsLoader({
  equipmentId,
  onChange,
}: {
  equipmentId: unknown;
  onChange: (data: LastCounterData[]) => void;
}): null {
  const lastCounters = useEquipmentLastCounters(equipmentId);

  useEffect(() => {
    onChange(lastCounters);
  }, [lastCounters, onChange]);

  return null;
}

function isCommonCounterTerm(term: Pick<ContractTermFormData, "kind">): boolean {
  return term.kind === COMMON_COUNTER_KIND;
}

function hasAmbiguousUsagePricing(term: ContractTermFormData): boolean {
  const hasRent = Boolean(term.rent_price && parsePriceInput(term.rent_price) > 0);
  if (!hasRent || term.prices.length === 0) return false;
  return !term.prices.some((p) => Number(p.included_in_rent) > 0);
}

function computeSettlementKindLabel(term: ContractTermFormData, t: (key: string) => string): string {
  const hasRent = Boolean(term.rent_price && parsePriceInput(term.rent_price) > 0);
  if (!hasRent) return t("Per usage");
  if (term.prices.length === 0) return t("Rent without limits");
  const hasUsageLimit = term.prices.some((p) => Number(p.included_in_rent) > 0);
  if (hasUsageLimit) return t("Rent with usage limit");
  return term.bill_usage_from_start ? t("Per usage") : t("Rent with amount limit");
}

function getTermEquipmentIds(term: ContractTermFormData): string[] {
  if (term.equipment_ids?.length) {
    return term.equipment_ids.map(String);
  }
  return (term.equipments || [])
    .map((equipment) => String(equipment.id))
    .filter(Boolean);
}

function termHasEquipment(term: ContractTermFormData): boolean {
  return getTermEquipmentIds(term).length > 0;
}

function dedupePricesByKind(prices: any[]): ContractTermPriceFormData[] {
  const byKind = new Map<string, ContractTermPriceFormData>();
  for (const price of prices) {
    const kind = price.kind || "";
    if (!kind || byKind.has(kind)) {
      continue;
    }
    byKind.set(kind, {
      id: price.id,
      kind,
      price: normalizeStoredPrice(price.price),
      price_input_mode: "netto",
      included_in_rent: normalizeStoredPrice(price.included_in_rent) || 0,
      has_end_counter: Boolean(price.end_counter),
    });
  }
  return Array.from(byKind.values());
}

function mapApiTermToFormData(term: any): ContractTermFormData {
  const toTime = (v: string) => (v ? String(v).slice(0, 5) : "");

  const equipments = Array.isArray(term.equipments) ? term.equipments : [];
  const equipmentIds = equipments
    .map((equipment: EquipmentOption) => String(equipment.id))
    .filter(Boolean);

  // installation_address is write-only on the API; the persisted address lives on the primary equipment.
  const equipmentAddress = equipments[0]?.installation_address ?? term.installation_address ?? null;

  const openingHours: OpeningHourRow[] = Array.isArray(equipmentAddress?.opening_hours)
    ? equipmentAddress.opening_hours.map((oh: any) => ({
        ...(oh.id != null && oh.id !== "" ? { id: String(oh.id) } : {}),
        week_day: oh.week_day ?? "",
        from_hour: toTime(oh.from_hour ?? ""),
        to_hour: toTime(oh.to_hour ?? ""),
      }))
    : [];

  return {
    id: term.id,
    active: term.active === false ? false : true,
    equipment_ids: equipmentIds,
    equipments,
    kind: term.kind || "",
    settlement_cycle: term.settlement_cycle || DEFAULT_SETTLEMENT_CYCLE,
    next_settlement_date: term.next_settlement_date || "",
    rent_price: normalizeStoredPrice(term.rent_price),
    rent_price_input_mode: "netto",
    bill_usage_from_start: term.settlement_kind === PER_USAGE_SETTLEMENT_KIND,
    installation_address: equipmentAddress
      ? {
          country: equipmentAddress.country || "",
          city: equipmentAddress.city || "",
          street: equipmentAddress.street || "",
          zip_code: equipmentAddress.zip_code || "",
          phone_number: equipmentAddress.phone_number || "",
          email: equipmentAddress.email || "",
          email_counter_reminders: equipmentAddress.email_counter_reminders || "",
          notes: equipmentAddress.notes || "",
          opening_hours: openingHours,
        }
      : null,
    prices: dedupePricesByKind(term.prices || []),
    counters: [],
    end_counters: [],
  };
}

function buildDraftCountersPayload(
  term: ContractTermFormData,
  drafts: CounterDraft[]
): Record<string, unknown>[] {
  return drafts
    .filter((counter) => counter.value != null && counter.value !== "" && Boolean(counter.kind))
    .filter((counter) => !isCommonCounterTerm(term) || Boolean(counter.equipment_id))
    .map((counter) => ({
      kind: counter.kind,
      provider: counter.provider ?? "C",
      value: counter.value,
      read_at: counter.read_at,
      ...(isCommonCounterTerm(term) && counter.equipment_id
        ? { equipment_id: counter.equipment_id }
        : {}),
    }));
}

function buildTermPayload(term: ContractTermFormData): Record<string, unknown> {
  const equipmentIds = getTermEquipmentIds(term);
  const isDeactivating = Boolean(term.id) && term.active === false;
  const countersToAttach = !term.id ? buildDraftCountersPayload(term, term.counters) : [];
  const endCountersToAttach = isDeactivating
    ? buildDraftCountersPayload(term, term.end_counters)
    : [];

  const termPayload: Record<string, unknown> = {
    ...(term.id && { id: term.id }),
    ...(equipmentIds.length > 0 ? { equipment_ids: equipmentIds } : {}),
    kind: term.kind,
    settlement_cycle: term.settlement_cycle,
    next_settlement_date: term.next_settlement_date,
    rent_price: term.rent_price,
    bill_usage_from_start: term.bill_usage_from_start,
    prices: term.prices.map((price) => ({
      ...(price.id && { id: price.id }),
      kind: price.kind,
      price: price.price,
      included_in_rent: price.included_in_rent,
    })),
    ...(countersToAttach.length > 0 ? { counters: countersToAttach } : {}),
    ...(endCountersToAttach.length > 0 ? { end_counters: endCountersToAttach } : {}),
    ...(term.id ? { active: term.active !== false } : {}),
  };

  // Closing a term releases its equipment: the backend clears the installation
  // address of everything coming off the contract, so sending one here would
  // only create an address row that is nulled out again in the same request.
  if (
    !isDeactivating &&
    term.installation_address &&
    Object.values(term.installation_address).some(Boolean)
  ) {
    const openingHours = term.installation_address.opening_hours || [];
    termPayload.installation_address = {
      ...term.installation_address,
      ...(openingHours.length > 0 && {
        opening_hours: openingHours.map((oh) => ({
          ...(oh.id != null && oh.id !== "" ? { id: oh.id } : {}),
          week_day: oh.week_day,
          from_hour: oh.from_hour,
          to_hour: oh.to_hour,
        })),
      }),
    };
  }

  return termPayload;
}

export interface ContractTermPriceFormData {
  id?: string;
  kind: string;
  price: string;
  price_input_mode?: PriceInputMode;
  included_in_rent: number | string;
  /** Read-only: whether this price already has an end counter set server-side. */
  has_end_counter?: boolean;
}

interface CounterDraft {
  equipment_id?: unknown;
  kind?: unknown;
  provider?: unknown;
  value?: unknown;
  read_at?: unknown;
}

interface ContractMultistepFormProps {
  onSuccess?: () => void;
  id?: string;
  clientId?: string;
}

export default function ContractMultistepForm({ onSuccess, id, clientId }: ContractMultistepFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { executeRequest } = useApiRequest();
  const { get: getContract } = useCRUD(CONTRACTS_ENDPOINT);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formOptions, setFormOptions] = useState<any>({});
  const [contractData, setContractData] = useState<ContractFormData>({
    client_id: clientId || null,
    numeration_schema_id: null,
    number: "",
    internal_name: "",
    start_date: "",
    end_date: "",
    indefinite_after_end_date: false,
    issuance_date: dayjs().format("YYYY-MM-DD"),
    signature_date: "",
    inflation_condition: "",
    inflation_rate_percent: "",
    is_brutto: false,
  });
  const [contractTerms, setContractTerms] = useState<ContractTermFormData[]>([
    createEmptyContractTerm(),
  ]);
  const [expandedTerms, setExpandedTerms] = useState<Record<string, boolean>>({});
  const [clients, setClients] = useState<any[]>([]);
  const [numerationSchemas, setNumerationSchemas] = useState<any[]>([]);
  const [initialNumerationSchemaId, setInitialNumerationSchemaId] = useState<string | null>(null);
  const [initialDataLoading, setInitialDataLoading] = useState<boolean>(!!id);
  const [draftRentPrice, setDraftRentPrice] = useState<Record<number, string>>({});
  const [draftPrice, setDraftPrice] = useState<Record<string, string>>({});
  const [fillAddressLoadingByTerm, setFillAddressLoadingByTerm] = useState<Record<number, boolean>>({});
  const [fillLastContractLoadingByTerm, setFillLastContractLoadingByTerm] = useState<Record<number, boolean>>({});
  const [latestClientContractNumber, setLatestClientContractNumber] = useState<string | null>(null);
  const [latestContractNumberLoading, setLatestContractNumberLoading] = useState(false);
  const [lastCountersByTerm, setLastCountersByTerm] = useState<Record<number, LastCounterData[]>>({});
  const [financialDataSourceByTerm, setFinancialDataSourceByTerm] = useState<Record<number, string>>({});
  const [financialDataSourceMenu, setFinancialDataSourceMenu] = useState<{
    termIndex: number;
    anchorEl: HTMLElement;
  } | null>(null);
  const [closeContractPromptDismissed, setCloseContractPromptDismissed] = useState(false);
  const isEditingContract = Boolean(id);
  const isSmallScreen = useMediaQuery("(max-width:599.95px)");

  const steps = [
    t("Contract Information"),
    t("Equipments"),
    t("Review & Submit"),
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contractResponse, termResponse] = await Promise.all([
          executeRequest(() => axios.options(CONTRACTS_ENDPOINT)),
          executeRequest(() => axios.options(CONTRACT_TERMS_ENDPOINT)),
        ]);

        setFormOptions({
          contract: contractResponse.data?.actions?.POST || {},
          term: termResponse.data?.actions?.POST || {},
        });

        if (id) {
          const contractResponseData = await getContract(id);
          if (contractResponseData?.data) {
            setContractData({
              client_id: contractResponseData.data.client_id || null,
              numeration_schema_id: contractResponseData.data.numeration_schema_id || null,
              number: contractResponseData.data.number || "",
              internal_name: contractResponseData.data.internal_name || "",
              start_date: contractResponseData.data.start_date || "",
              end_date: contractResponseData.data.end_date || "",
              indefinite_after_end_date: Boolean(
                contractResponseData.data.indefinite_after_end_date
              ),
              issuance_date: contractResponseData.data.issuance_date || dayjs().format("YYYY-MM-DD"),
              signature_date: contractResponseData.data.signature_date || "",
              inflation_condition: contractResponseData.data.inflation_condition ?? "",
              inflation_rate_percent:
                contractResponseData.data.inflation_rate_percent != null
                  ? String(contractResponseData.data.inflation_rate_percent)
                  : "",
              is_brutto: Boolean(contractResponseData.data.is_brutto),
            });

            setInitialNumerationSchemaId(contractResponseData.data.numeration_schema_id || null);

            const fetchRequests = [];
            if (contractResponseData.data.client_id) {
              fetchRequests.push(
                executeRequest(() => axios.get(`${CLIENTS_ENDPOINT}${contractResponseData.data.client_id}/`))
              );
            } else {
              fetchRequests.push(null);
            }
            if (contractResponseData.data.numeration_schema_id) {
              fetchRequests.push(
                executeRequest(() => axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${contractResponseData.data.numeration_schema_id}/`))
              );
            } else {
              fetchRequests.push(null);
            }

            const [clientResponse, schemaResponse] = await Promise.all(fetchRequests);
            if (clientResponse?.data) {
              setClients([clientResponse.data]);
            }
            if (schemaResponse?.data) {
              setNumerationSchemas([schemaResponse.data]);
            }

            if (contractResponseData.data.terms) {
              const mappedTerms =
                contractResponseData.data.terms.length > 0
                  ? contractResponseData.data.terms.map(mapApiTermToFormData)
                  : [createEmptyContractTerm()];
              setContractTerms(mappedTerms);
              setExpandedTerms(
                Object.fromEntries(
                  mappedTerms.map((term: ContractTermFormData, termIndex: number) => [
                    getPersistedTermKey(term, termIndex),
                    term.active !== false,
                  ])
                )
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchData();
  }, [id, clientId, executeRequest, getContract]);

  useEffect(() => {
    if (clientId && clients.length === 0) {
      const fetchClient = async () => {
        try {
          const response = await executeRequest(() => axios.get(`${CLIENTS_ENDPOINT}${clientId}/`));
          if (response?.data) {
            setClients([response.data]);
          }
        } catch (error) {
          console.error("Failed to fetch client:", error);
        }
      };
      fetchClient();
    }
  }, [clientId, clients.length, executeRequest]);

  useEffect(() => {
    if (!contractData.client_id) {
      setLatestClientContractNumber(null);
      setLatestContractNumberLoading(false);
      return;
    }

    let cancelled = false;

    const fetchLatestNumber = async () => {
      setLatestContractNumberLoading(true);
      try {
        const { data } = await executeRequest(() =>
          axios.get(`${CONTRACTS_ENDPOINT}latest-by-client/`, {
            params: {
              client_id: contractData.client_id,
              ...(id ? { exclude_contract_id: id } : {}),
            },
          })
        );
        if (!cancelled) {
          const raw = data?.latest_number;
          setLatestClientContractNumber(
            raw !== null && raw !== undefined && String(raw).trim() !== ""
              ? String(raw)
              : null
          );
        }
      } catch {
        if (!cancelled) {
          setLatestClientContractNumber(null);
        }
      } finally {
        if (!cancelled) {
          setLatestContractNumberLoading(false);
        }
      }
    };

    fetchLatestNumber();
    return () => {
      cancelled = true;
    };
  }, [contractData.client_id, executeRequest, id]);

  useEffect(() => {
    const fetchNextNumber = async () => {
      if (!contractData.numeration_schema_id) return;
      if (id && initialDataLoading) return;

      const schemaChanged = contractData.numeration_schema_id !== initialNumerationSchemaId;

      if (!id || schemaChanged) {
        try {
          const { data } = await executeRequest(() =>
            axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${contractData.numeration_schema_id}/next/`)
          );
          setContractData((prev) => ({ ...prev, number: data?.value || data?.result || data }));
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            t("Failed to fetch next contract number");
          console.error("Failed to fetch next contract number:", errorMessage);
        }
      }
    };

    fetchNextNumber();
  }, [contractData.numeration_schema_id, id, executeRequest, t, initialNumerationSchemaId, initialDataLoading]);

  // Counter/end_counter rows can be auto-created (on equipment select, or on
  // deactivation) before the equipment's last known counter values have
  // finished loading. Once they arrive, backfill any row that's still empty -
  // never a row the user has already typed a value into.
  useEffect(() => {
    setContractTerms((prev) => {
      let changed = false;
      const next = prev.map((term, termIndex) => {
        const lastCounters = lastCountersByTerm[termIndex];
        if (!lastCounters || lastCounters.length === 0) {
          return term;
        }

        const backfill = (drafts: CounterDraft[]): [CounterDraft[], boolean] => {
          let touched = false;
          const filled = drafts.map((draft) => {
            if (draft.value != null && draft.value !== "") {
              return draft;
            }
            const match = lastCounters.find((counter) => counter.kind === draft.kind);
            if (!match) {
              return draft;
            }
            touched = true;
            return { ...draft, value: match.value };
          });
          return [touched ? filled : drafts, touched];
        };

        const [nextCounters, countersTouched] = backfill(term.counters);
        const [nextEndCounters, endCountersTouched] = backfill(term.end_counters);
        if (!countersTouched && !endCountersTouched) {
          return term;
        }

        changed = true;
        return { ...term, counters: nextCounters, end_counters: nextEndCounters };
      });

      return changed ? next : prev;
    });
  }, [lastCountersByTerm]);

  const handleNext = () => {
    if (activeStep === 0 && !validateContractData()) {
      enqueueSnackbar(t("Please fill in all required contract fields"), ERROR_SNACKBAR);
      return;
    }
    if (activeStep === 1 && !validateContractTerms()) {
      enqueueSnackbar(t("Please fill in all required equipment fields"), ERROR_SNACKBAR);
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateContractData = () => {
    return contractData.client_id && contractData.start_date && contractData.issuance_date && contractData.number;
  };

  const validateContractTerms = () => {
    if (contractTerms.length === 0) return false;
    return contractTerms.every((term) => {
      if (
        !termHasEquipment(term) ||
        !term.kind ||
        !term.settlement_cycle ||
        !term.next_settlement_date ||
        !(term.rent_price || term.prices.some((price) => price.price))
      ) {
        return false;
      }

      if (isCommonCounterTerm(term) && getTermEquipmentIds(term).length < 1) {
        return false;
      }

      // Deactivating a term releases its equipment - the backend clears the
      // installation address right after, so it isn't required on the way out.
      const isDeactivatingTerm = Boolean(term.id) && term.active === false;
      if (!isDeactivatingTerm) {
        const { city, zip_code, street } = term.installation_address || {};
        if (!street || !city || !zip_code) {
          return false;
        }
      }

      const filledCounters = term.counters.filter(
        (counter) => counter.value != null && counter.value !== "" && Boolean(counter.kind)
      );
      if (
        filledCounters.some(
          (counter) => isCommonCounterTerm(term) && !counter.equipment_id
        )
      ) {
        return false;
      }

      // A brand-new term's prices are always anchored to a counter created
      // right here - never to some pre-existing reading - so every price kind
      // needs a matching filled counter in this same submission.
      if (!term.id && term.prices.length > 0) {
        const filledCounterKinds = new Set(filledCounters.map((counter) => counter.kind));
        const hasPriceWithoutCounter = term.prices.some(
          (price) => price.kind && !filledCounterKinds.has(price.kind)
        );
        if (hasPriceWithoutCounter) {
          return false;
        }
      }

      if (term.id && term.active === false) {
        const filledEndCounters = term.end_counters.filter(
          (counter) => counter.value != null && counter.value !== "" && Boolean(counter.kind)
        );
        if (
          filledEndCounters.some(
            (counter) => isCommonCounterTerm(term) && !counter.equipment_id
          )
        ) {
          return false;
        }
        const pricesAlreadyEnded = term.prices.every((price) => price.has_end_counter);
        if (!pricesAlreadyEnded && filledEndCounters.length === 0) {
          return false;
        }
      }

      return true;
    });
  };

  const handleAddTerm = () => {
    setContractTerms([...contractTerms, createEmptyContractTerm()]);
  };

  const handleRemoveTerm = (index: number) => {
    const newTerms = [...contractTerms];
    newTerms.splice(index, 1);
    setContractTerms(newTerms);
  };

  const handleTermChange = (index: number, field: string, value: any) => {
    if (field === "kind" && value === COMMON_COUNTER_KIND) {
      const hasOtherCcr = contractTerms.some(
        (term, termIndex) => termIndex !== index && isCommonCounterTerm(term)
      );
      if (hasOtherCcr) {
        enqueueSnackbar(
          t("Only one common counter equipment group is allowed per contract"),
          ERROR_SNACKBAR
        );
        return;
      }
    }

    const newTerms = [...contractTerms];
    const currentTerm = newTerms[index];

    if (field === "kind" && value === COMMON_COUNTER_KIND && getTermEquipmentIds(currentTerm).length > 0) {
      newTerms[index] = {
        ...currentTerm,
        kind: value,
        equipment_ids: getTermEquipmentIds(currentTerm),
        equipments: currentTerm.equipments || [],
      };
      setContractTerms(newTerms);
      return;
    }

    if (
      field === "kind" &&
      value !== COMMON_COUNTER_KIND &&
      isCommonCounterTerm(currentTerm)
    ) {
      const firstEquipment = currentTerm.equipments?.[0];
      newTerms[index] = {
        ...currentTerm,
        kind: value,
        equipment_ids: firstEquipment ? [String(firstEquipment.id)] : [],
        equipments: firstEquipment ? [firstEquipment] : [],
      };
      setContractTerms(newTerms);
      return;
    }

    newTerms[index] = { ...newTerms[index], [field]: value };
    setContractTerms(newTerms);
  };

  const handleCcrEquipmentsChange = (termIndex: number, equipments: EquipmentOption[]) => {
    const newTerms = [...contractTerms];
    newTerms[termIndex] = autoPopulateNewTermDrafts(
      {
        ...newTerms[termIndex],
        equipments,
        equipment_ids: equipments.map((equipment) => String(equipment.id)),
      },
      termIndex
    );
    setContractTerms(newTerms);
  };

  const isPersistedEditableTerm = (term: ContractTermFormData) =>
    isEditingContract && Boolean(term.id);

  const getTermSummaryText = (term: ContractTermFormData, kindChoice?: { display_name?: string }) => {
    if (isCommonCounterTerm(term)) {
      return t("Common counter for {{count}} equipments", {
        count: getTermEquipmentIds(term).length,
      });
    }

    const names = (term.equipments || [])
      .map((equipment) => equipment.full_name || equipment.name)
      .filter(Boolean);
    const equipmentLabel = names.length > 0 ? names.join(", ") : undefined;
    return [equipmentLabel, kindChoice?.display_name].filter(Boolean).join(" • ");
  };

  const getTermEquipmentLabel = (term: ContractTermFormData) => {
    const names = (term.equipments || [])
      .map((equipment) => equipment.full_name || equipment.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : "-";
  };

  const getTermEquipmentLabelById = (term: ContractTermFormData, equipmentId: unknown) => {
    const normalizedId = equipmentId != null ? String(equipmentId) : "";
    if (!normalizedId) {
      return "-";
    }
    const equipment = (term.equipments || []).find(
      (item) => String(item.id) === normalizedId
    );
    return equipment?.full_name || equipment?.name || normalizedId;
  };

  const termHasNewEquipments = (term: ContractTermFormData) => !term.id;

  const termHasFinancialData = (term: ContractTermFormData) =>
    Boolean(
      term.rent_price ||
        term.prices.some((price) => price.price !== "" && price.price != null)
    );

  const getFinancialDataSources = (termIndex: number) =>
    contractTerms.filter(
      (candidate, candidateIndex) =>
        candidateIndex !== termIndex &&
        termHasEquipment(candidate) &&
        termHasFinancialData(candidate)
    );

  const handleFinancialDataSourceChange = (termIndex: number, sourceIndex: string) => {
    setFinancialDataSourceByTerm((prev) => ({ ...prev, [termIndex]: sourceIndex }));
    if (!sourceIndex) return;

    const sourceTerm = contractTerms[Number(sourceIndex)];
    if (!sourceTerm) return;

    setContractTerms((prev) => {
      const next = [...prev];
      const destinationTerm = next[termIndex];
      next[termIndex] = {
        ...destinationTerm,
        settlement_cycle: sourceTerm.settlement_cycle,
        rent_price: sourceTerm.rent_price,
        rent_price_input_mode: sourceTerm.rent_price_input_mode,
        bill_usage_from_start: sourceTerm.bill_usage_from_start,
        prices: sourceTerm.prices.map((price) => ({ ...price })),
      };
      return next;
    });

    setDraftRentPrice((prev) => {
      const next = { ...prev };
      delete next[termIndex];
      return next;
    });
    setDraftPrice((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(`${termIndex}-`))
      )
    );
  };

  const handleTermActiveToggle = (index: number, active: boolean) => {
    setContractTerms((prev) => {
      const next = [...prev];
      const term = next[index];
      const shouldAutoFillEndCounters = !active && term.end_counters.length === 0;
      next[index] = {
        ...term,
        active,
        ...(shouldAutoFillEndCounters
          ? { end_counters: buildAutoCounterDrafts(term, index) }
          : {}),
      };
      return next;
    });
  };

  const handleTermAccordionChange = (termKey: string, expanded: boolean) => {
    setExpandedTerms((prev) => ({ ...prev, [termKey]: expanded }));
  };

  const createEmptyPrice = (kind: string): ContractTermPriceFormData => ({
    kind,
    price: "",
    price_input_mode: "netto",
    included_in_rent: 0,
  });

  // Same kind-based defaulting as buildAutoCounterDrafts: Black equipment gets
  // one "BP" price row, Color gets "BP" + "CP"; anything else (common counter,
  // no equipment yet) falls back to one blank row.
  const buildAutoPriceDrafts = (
    term: ContractTermFormData
  ): ContractTermPriceFormData[] => {
    if (!isCommonCounterTerm(term)) {
      const equipmentKind = term.equipments?.[0]?.kind;
      if (equipmentKind === EQUIPMENT_KIND_BLACK) {
        return [createEmptyPrice("BP")];
      }
      if (equipmentKind === EQUIPMENT_KIND_COLOR) {
        return [createEmptyPrice("BP"), createEmptyPrice("CP")];
      }
    }

    return [createEmptyPrice("")];
  };

  const handleAddPrice = (termIndex: number) => {
    const newTerms = [...contractTerms];
    const term = newTerms[termIndex];
    term.prices.push(
      ...(term.prices.length === 0 ? buildAutoPriceDrafts(term) : [createEmptyPrice("")])
    );
    setContractTerms(newTerms);
  };

  const handleRemovePrice = (termIndex: number, priceIndex: number) => {
    const newTerms = [...contractTerms];
    newTerms[termIndex].prices.splice(priceIndex, 1);
    setContractTerms(newTerms);
  };

  const handlePriceChange = (
    termIndex: number,
    priceIndex: number,
    field: string,
    value: any
  ) => {
    const newTerms = [...contractTerms];
    newTerms[termIndex].prices[priceIndex] = {
      ...newTerms[termIndex].prices[priceIndex],
      [field]: value,
    };
    setContractTerms(newTerms);
  };

  const getDefaultCounterValue = (termIndex: number, kind: string) =>
    lastCountersByTerm[termIndex]?.find((counter) => counter.kind === kind)?.value;

  // Same auto-defaulting for both flows: a single equipment of kind Black gets
  // one pre-filled "BP" row, Color gets "BP" + "CP", each pre-filled from the
  // equipment's latest known counter value; anything else (common counter, no
  // equipment yet) falls back to one blank row for the user to fill in.
  const buildAutoCounterDrafts = (
    term: ContractTermFormData,
    termIndex: number
  ): CounterDraft[] => {
    if (isCommonCounterTerm(term)) {
      return [{}];
    }

    const equipmentKind = term.equipments?.[0]?.kind;
    if (equipmentKind === EQUIPMENT_KIND_BLACK) {
      const bpValue = getDefaultCounterValue(termIndex, "BP");
      return [{ kind: "BP", ...(bpValue != null ? { value: bpValue } : {}) }];
    }
    if (equipmentKind === EQUIPMENT_KIND_COLOR) {
      const bpValue = getDefaultCounterValue(termIndex, "BP");
      const cpValue = getDefaultCounterValue(termIndex, "CP");
      return [
        { kind: "BP", ...(bpValue != null ? { value: bpValue } : {}) },
        { kind: "CP", ...(cpValue != null ? { value: cpValue } : {}) },
      ];
    }

    return [{}];
  };

  // Once a brand-new term gets its equipment (kind is now known), show its
  // prices and counters right away instead of waiting for "Add Price"/"Add
  // counter" clicks - reuses the exact same kind-based defaulting/pre-fill.
  const autoPopulateNewTermDrafts = (
    term: ContractTermFormData,
    termIndex: number
  ): ContractTermFormData => {
    if (term.id || !termHasEquipment(term)) {
      return term;
    }

    return {
      ...term,
      prices: term.prices.length === 0 ? buildAutoPriceDrafts(term) : term.prices,
      counters:
        term.counters.length === 0 ? buildAutoCounterDrafts(term, termIndex) : term.counters,
    };
  };

  const handleAddCounter = (termIndex: number) => {
    const newTerms = [...contractTerms];
    const term = newTerms[termIndex];
    term.counters.push(
      ...(term.counters.length === 0 ? buildAutoCounterDrafts(term, termIndex) : [{}])
    );
    setContractTerms(newTerms);
  };

  const handleRemoveCounter = (termIndex: number, counterIndex: number) => {
    const newTerms = [...contractTerms];
    newTerms[termIndex].counters.splice(counterIndex, 1);
    setContractTerms(newTerms);
  };

  const handleCounterChange = (
    termIndex: number,
    counterIndex: number,
    data: Record<string, unknown>
  ) => {
    setContractTerms((prev) => {
      if (prev[termIndex]?.counters?.[counterIndex] === data) {
        return prev;
      }
      const next = [...prev];
      next[termIndex] = {
        ...next[termIndex],
        counters: [...next[termIndex].counters],
      };
      next[termIndex].counters[counterIndex] = data;
      return next;
    });
  };

  const handleAddEndCounter = (termIndex: number) => {
    const newTerms = [...contractTerms];
    const term = newTerms[termIndex];
    term.end_counters.push(
      ...(term.end_counters.length === 0 ? buildAutoCounterDrafts(term, termIndex) : [{}])
    );
    setContractTerms(newTerms);
  };

  const handleRemoveEndCounter = (termIndex: number, counterIndex: number) => {
    const newTerms = [...contractTerms];
    newTerms[termIndex].end_counters.splice(counterIndex, 1);
    setContractTerms(newTerms);
  };

  const handleEndCounterChange = (
    termIndex: number,
    counterIndex: number,
    data: Record<string, unknown>
  ) => {
    setContractTerms((prev) => {
      if (prev[termIndex]?.end_counters?.[counterIndex] === data) {
        return prev;
      }
      const next = [...prev];
      next[termIndex] = {
        ...next[termIndex],
        end_counters: [...next[termIndex].end_counters],
      };
      next[termIndex].end_counters[counterIndex] = data;
      return next;
    });
  };

  const getInstallationAddressFromClient = (client: any) => {
    const clientAddress = client?.address
      ? { ...client.address, opening_hours: client.address.opening_hours ?? client?.opening_hours }
      : null;
    return mapAddressToFormAddress(clientAddress);
  };

  const handleFillFromClientAddress = async (termIndex: number, selectedClient: any) => {
    if (!selectedClient?.id) {
      enqueueSnackbar(t("Client address is not available"), ERROR_SNACKBAR);
      return;
    }

    setFillAddressLoadingByTerm((prev) => ({ ...prev, [termIndex]: true }));

    try {
      let clientToUse = selectedClient;

      if (!Array.isArray(selectedClient?.address?.opening_hours)) {
        const response = await executeRequest(() =>
          axios.get(`${CLIENTS_ENDPOINT}${selectedClient.id}/`)
        );
        if (response?.data) {
          clientToUse = response.data;
          setClients((prev) => {
            const withoutCurrent = prev.filter((c) => String(c.id) !== String(clientToUse.id));
            return [clientToUse, ...withoutCurrent];
          });
        }
      }

      const addressFromClient = getInstallationAddressFromClient(clientToUse);
      if (!addressFromClient) {
        enqueueSnackbar(t("Client address is not available"), ERROR_SNACKBAR);
        return;
      }
      handleTermChange(termIndex, "installation_address", addressFromClient);
    } catch (error) {
      console.error("Failed to fetch client details:", error);
      enqueueSnackbar(t("Failed to fetch client data"), ERROR_SNACKBAR);
    } finally {
      setFillAddressLoadingByTerm((prev) => ({ ...prev, [termIndex]: false }));
    }
  };

  const handleFillFromPreviousPositionAddress = (termIndex: number) => {
    const previousTerm = contractTerms[termIndex - 1];
    if (!previousTerm?.installation_address) {
      enqueueSnackbar(t("The previous position has no installation address"), ERROR_SNACKBAR);
      return;
    }
    handleTermChange(termIndex, "installation_address", {
      ...previousTerm.installation_address,
      opening_hours: (previousTerm.installation_address.opening_hours || []).map((oh) => ({ ...oh })),
    });
  };

  const handleFillFromLastContractAddress = async (termIndex: number, selectedClientId: string | null) => {
    if (!selectedClientId) {
      enqueueSnackbar(t("Client address is not available"), ERROR_SNACKBAR);
      return;
    }

    setFillLastContractLoadingByTerm((prev) => ({ ...prev, [termIndex]: true }));

    try {
      const response = await executeRequest(() =>
        axios.get(`${CONTRACTS_ENDPOINT}latest-address-by-client/`, {
          params: {
            client_id: selectedClientId,
            ...(id ? { exclude_contract_id: id } : {}),
          },
        })
      );

      const addressFromLastContract = mapAddressToFormAddress(response?.data?.installation_address);
      if (!addressFromLastContract) {
        enqueueSnackbar(
          t("No installation address found in this client's previous contracts"),
          ERROR_SNACKBAR
        );
        return;
      }
      handleTermChange(termIndex, "installation_address", addressFromLastContract);
    } catch (error) {
      console.error("Failed to fetch last contract address:", error);
      enqueueSnackbar(t("Failed to fetch client data"), ERROR_SNACKBAR);
    } finally {
      setFillLastContractLoadingByTerm((prev) => ({ ...prev, [termIndex]: false }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const contractPayload: any = {
        client_id: contractData.client_id,
        numeration_schema_id: contractData.numeration_schema_id,
        number: contractData.number || undefined,
        internal_name: contractData.internal_name || undefined,
        start_date: contractData.start_date,
        end_date: contractData.end_date || null,
        indefinite_after_end_date: contractData.indefinite_after_end_date,
        issuance_date: contractData.issuance_date || undefined,
        signature_date: contractData.signature_date || undefined,
        inflation_condition: contractData.inflation_condition || null,
        inflation_rate_percent: contractData.inflation_condition
          ? contractData.inflation_rate_percent || null
          : null,
        is_brutto: contractData.is_brutto,
      };

      let isUpdating = !!id;

      contractPayload.terms = contractTerms
        .filter(termHasEquipment)
        .map((term) => buildTermPayload(term));

      const contractResponse = isUpdating
        ? await executeRequest(() =>
            axios.patch(`${CONTRACTS_ENDPOINT}${id}/save-with-terms/`, contractPayload)
          )
        : await executeRequest(() =>
            axios.post(CONTRACTS_SAVE_WITH_TERMS_ENDPOINT, contractPayload)
          );

      if (!contractResponse?.success) {
        const error: any = new Error(
          isUpdating ? "Failed to update contract" : "Failed to create contract"
        );
        error.response = { data: contractResponse?.errorData };
        throw error;
      }

      if (!contractResponse?.data?.id) {
        throw new Error(isUpdating ? "Failed to update contract" : "Failed to create contract");
      }

      enqueueSnackbar(
        isUpdating ? t("Contract updated successfully") : t("Contract created successfully"),
        SUCCESS_SNACKBAR
      );
      onSuccess?.();
    } catch (error: any) {
      let errorMessage = id ? t("Failed to update contract") : t("Failed to create contract");

      if (error?.response?.data) {
        const messages = extractErrorMessages(error.response.data);
        if (messages.length > 0) {
          errorMessage = messages.join("; ");
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, ERROR_SNACKBAR);
    } finally {
      setLoading(false);
    }
  };

  const renderContractStep = () => {
    const selectedClient = clients.find((c) => String(c.id) === String(contractData.client_id));
    const selectedSchema = numerationSchemas.find(
      (s) => String(s.id) === String(contractData.numeration_schema_id)
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <CustomAutocomplete
          size="small"
          apiConfig={{
            url: CLIENTS_ENDPOINT,
            minSearchChars: 2,
            debounceMs: 300,
          }}
          onOptionsChange={setClients}
          options={clients}
          value={selectedClient || null}
          onChange={(_, newValue) =>
            setContractData({ ...contractData, client_id: newValue?.id || null })
          }
          getOptionLabel={(option) => option.short_name || option.name || ""}
          noOptionsText={t("No options")}
          renderInput={(params) => (
            <TextField {...params} label={t("Client")} required />
          )}
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <CustomAutocomplete
            size="small"
            apiConfig={{
              url: NUMERATION_SCHEMA_ENDPOINT,
              minSearchChars: 2,
              debounceMs: 300,
            }}
            onOptionsChange={setNumerationSchemas}
            options={numerationSchemas}
            value={selectedSchema || null}
            onChange={(_, newValue) =>
              setContractData({
                ...contractData,
                numeration_schema_id: newValue?.id || null,
              })
            }
            getOptionLabel={(option) => option.name || ""}
            noOptionsText={t("No options")}
            renderInput={(params) => (
              <TextField {...params} label={t("Numeration schema")} />
            )}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />

          <TextField
            size="small"
            label={t("Number")}
            value={contractData.number}
            onChange={(e) =>
              setContractData({ ...contractData, number: e.target.value })
            }
            disabled={!!contractData.numeration_schema_id}
            required
            helperText={
              contractData.client_id ? (
                <AnimatedHelperText visible>
                  {latestContractNumberLoading
                    ? t("Loading...")
                    : latestClientContractNumber
                      ? t("Latest contract number for this client: {{number}}", {
                          number: latestClientContractNumber,
                        })
                      : t("This client has no contracts yet.")}
                </AnimatedHelperText>
              ) : undefined
            }
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />
        </Box>

        <TextField
          size="small"
          label={t("Internal name")}
          value={contractData.internal_name}
          onChange={(e) =>
            setContractData({ ...contractData, internal_name: e.target.value })
          }
          fullWidth
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <CustomDatePicker
            label={t("Start date")}
            value={contractData.start_date ? dayjs(contractData.start_date) : null}
            onChange={(value: Dayjs | null) =>
              setContractData({
                ...contractData,
                start_date: value?.format("YYYY-MM-DD") || "",
              })
            }
            slotProps={{
              textField: { size: "small", fullWidth: true, required: true },
            }}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />

          <ContractEndDatePicker
            value={contractData.end_date ? dayjs(contractData.end_date) : null}
            onChange={(value: Dayjs | null) =>
              setContractData({
                ...contractData,
                end_date: value?.format("YYYY-MM-DD") || "",
              })
            }
            indefiniteAfterEndDate={contractData.indefinite_after_end_date}
            onIndefiniteAfterEndDateChange={(checked) =>
              setContractData({ ...contractData, indefinite_after_end_date: checked })
            }
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <CustomDatePicker
            label={t("Issuance date")}
            value={contractData.issuance_date ? dayjs(contractData.issuance_date) : null}
            onChange={(value: Dayjs | null) =>
              setContractData({
                ...contractData,
                issuance_date: value?.format("YYYY-MM-DD") || "",
              })
            }
            slotProps={{
              textField: { size: "small", fullWidth: true, required: true },
            }}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />

          <CustomDatePicker
            label={t("Signature date")}
            value={contractData.signature_date ? dayjs(contractData.signature_date) : null}
            onChange={(value: Dayjs | null) =>
              setContractData({
                ...contractData,
                signature_date: value?.format("YYYY-MM-DD") || "",
              })
            }
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid #ccc",
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Switch
            checked={contractData.is_brutto}
            onChange={(_, checked) =>
              setContractData({ ...contractData, is_brutto: checked })
            }
            inputProps={{ "aria-label": t("Store values as gross (brutto)") }}
          />
          <Box>
            <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
              {t("Store values as gross (brutto)")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t(
                "For clients that require gross-only settlements (e.g. government-related entities). Rent and prices will be entered and stored as gross amounts, with no net/gross conversion."
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderContractTermsStep = () => {
    const kindChoices = formOptions.term?.kind?.choices || [];
    const settlementCycleChoices = formOptions.term?.settlement_cycle?.choices || [];
    const inflationRateChoices = formOptions.contract?.inflation_condition?.choices || [];
    const selectedClient = clients.find((c) => String(c.id) === String(contractData.client_id));
    const equipmentCounterKindChoices = [
      { value: "BP", display_name: t("Black pages") },
      { value: "CP", display_name: t("Color pages") },
      { value: "SP", display_name: t("Scanned pages") },
    ];

    const renderTermBody = (term: ContractTermFormData, termIndex: number) => (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          {isCommonCounterTerm(term) ? (
            <EquipmentAutocomplete
              multiple
              value={term.equipments || []}
              onChange={(newEquipments) =>
                handleCcrEquipmentsChange(termIndex, newEquipments as EquipmentOption[])
              }
              required
              showAddEquipmentButton={true}
            />
          ) : (
            <EquipmentAutocomplete
              value={term.equipments?.[0] || null}
              onChange={(newEquipment) => {
                const newTerms = [...contractTerms];
                const currentTerm = newTerms[termIndex];
                const previousEquipmentId = currentTerm.equipments?.[0]?.id;
                const equipmentChanged =
                  !currentTerm.id &&
                  previousEquipmentId != null &&
                  String(newEquipment?.id ?? "") !== String(previousEquipmentId);

                if (equipmentChanged) {
                  // Counters/prices already on this term were built for the
                  // previous equipment's kind and latest readings - drop them
                  // (along with the stale last-known-counters cache) so
                  // autoPopulateNewTermDrafts rebuilds fresh ones for the
                  // newly selected equipment instead of leaving stale values
                  // or momentarily pulling the old equipment's readings in.
                  setLastCountersByTerm((prev) => {
                    if (!(termIndex in prev)) return prev;
                    const next = { ...prev };
                    delete next[termIndex];
                    return next;
                  });
                }

                newTerms[termIndex] = autoPopulateNewTermDrafts(
                  {
                    ...currentTerm,
                    equipments: newEquipment ? [newEquipment as EquipmentOption] : [],
                    equipment_ids: newEquipment ? [String(newEquipment.id)] : [],
                    ...(equipmentChanged ? { counters: [], prices: [] } : {}),
                  },
                  termIndex
                );
                setContractTerms(newTerms);
              }}
              required
              showAddEquipmentButton={true}
            />
          )}

          <FormControl size="small" required sx={{ flex: { xs: "1 1 100%", sm: 1 } }}>
            <InputLabel sx={{ color: "var(--textColor)" }}>{t("Kind")}</InputLabel>
            <Select
              value={term.kind}
              onChange={(e) => handleTermChange(termIndex, "kind", e.target.value)}
              label={t("Kind")}
            >
              {kindChoices.map((choice: any) => {
                const isDisabled =
                  choice.value === COMMON_COUNTER_KIND &&
                  contractTerms.some(
                    (existingTerm, existingIndex) =>
                      existingIndex !== termIndex && isCommonCounterTerm(existingTerm)
                  );
                return (
                  <MenuItem key={choice.value} value={choice.value} disabled={isDisabled}>
                    {choice.display_name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <FormControl
            size="small"
            required
            disabled
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          >
            <InputLabel sx={{ color: "var(--textColor)" }}>{t("Settlement cycle")}</InputLabel>
            <Select
              value={term.settlement_cycle}
              onChange={(e) => handleTermChange(termIndex, "settlement_cycle", e.target.value)}
              label={t("Settlement cycle")}
            >
              {settlementCycleChoices.map((choice: any) => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <CustomDatePicker
            label={t("Next settlement date")}
            value={term.next_settlement_date ? dayjs(term.next_settlement_date) : null}
            onChange={(newValue: Dayjs | null) =>
              handleTermChange(
                termIndex,
                "next_settlement_date",
                newValue ? newValue.format("YYYY-MM-DD") : ""
              )
            }
            slotProps={{
              textField: { size: "small", required: true, fullWidth: true },
            }}
            sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
          />
        </Box>

        {termHasNewEquipments(term) && termHasEquipment(term) && getFinancialDataSources(termIndex).length > 0 && (
          <Box>
            <Button
              size="small"
              variant="outlined"
              onClick={(event) =>
                setFinancialDataSourceMenu((current) =>
                  current?.termIndex === termIndex
                    ? null
                    : { termIndex, anchorEl: event.currentTarget }
                )
              }
            >
              {t("Copy financial data")}
            </Button>
            <Popover
              open={financialDataSourceMenu?.termIndex === termIndex}
              anchorEl={financialDataSourceMenu?.anchorEl}
              onClose={() => setFinancialDataSourceMenu(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <FormControl size="small" sx={{ p: 2, minWidth: "min(320px, calc(100vw - 64px))" }}>
                <Select
                  autoFocus
                  displayEmpty
                  value={financialDataSourceByTerm[termIndex] || ""}
                  onChange={(event) => {
                    handleFinancialDataSourceChange(termIndex, String(event.target.value));
                    setFinancialDataSourceMenu(null);
                  }}
                >
                  <MenuItem value="">{t("Select a position")}</MenuItem>
                  {contractTerms.map((candidate, candidateIndex) => {
                    if (
                      candidateIndex === termIndex ||
                      !termHasEquipment(candidate) ||
                      !termHasFinancialData(candidate)
                    ) {
                      return null;
                    }

                    return (
                      <MenuItem key={candidateIndex} value={candidateIndex}>
                        {t("Contract Position")} {candidateIndex + 1}: {getTermEquipmentLabel(candidate)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Popover>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {contractData.is_brutto ? (
              <TextField
                size="small"
                label={t("Rent price (gross)")}
                value={formatPriceForDisplay(term.rent_price)}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  if (nextValue === "") {
                    handleTermChange(termIndex, "rent_price", "");
                    return;
                  }
                  if (!DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) return;
                  handleTermChange(termIndex, "rent_price", nextValue.replace(",", "."));
                }}
                fullWidth
                type="text"
                inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
              />
            ) : (
              <Box sx={{ display: "flex", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                <TextField
                  size="small"
                  label={t("Rent price")}
                  value={
                    (term.rent_price_input_mode || "netto") === "netto"
                      ? formatPriceForDisplay(term.rent_price)
                      : draftRentPrice[termIndex] !== undefined && draftRentPrice[termIndex] !== ""
                        ? formatPriceForDisplay(draftRentPrice[termIndex])
                        : term.rent_price
                          ? formatPriceForDisplay(nettoToBrutto(parsePriceInput(term.rent_price)))
                          : ""
                  }
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    const mode = term.rent_price_input_mode || "netto";
                    if (nextValue === "") {
                      handleTermChange(termIndex, "rent_price", "");
                      setDraftRentPrice((prev) => {
                        const next = { ...prev };
                        delete next[termIndex];
                        return next;
                      });
                      return;
                    }
                    if (!DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) return;
                    if (mode === "brutto") {
                      setDraftRentPrice((prev) => ({ ...prev, [termIndex]: nextValue }));
                    } else {
                      handleTermChange(termIndex, "rent_price", nextValue.replace(",", "."));
                      setDraftRentPrice((prev) => {
                        const next = { ...prev };
                        delete next[termIndex];
                        return next;
                      });
                    }
                  }}
                  onBlur={() => {
                    const mode = term.rent_price_input_mode || "netto";
                    const draft = draftRentPrice[termIndex];
                    if (mode === "brutto" && draft !== undefined && draft !== "") {
                      const parsed = parsePriceInput(draft);
                      if (!Number.isNaN(parsed)) {
                        handleTermChange(termIndex, "rent_price", bruttoToNetto(parsed));
                      }
                      setDraftRentPrice((prev) => {
                        const next = { ...prev };
                        delete next[termIndex];
                        return next;
                      });
                    }
                  }}
                  fullWidth
                  type="text"
                  inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <ToggleButtonGroup
                  value={term.rent_price_input_mode || "netto"}
                  exclusive
                  onChange={(_, newMode: PriceInputMode | null) => {
                    if (!newMode) return;
                    const draft = draftRentPrice[termIndex];
                    if (
                      (term.rent_price_input_mode || "netto") === "brutto" &&
                      draft !== undefined &&
                      draft !== ""
                    ) {
                      const parsed = parsePriceInput(draft);
                      if (!Number.isNaN(parsed)) {
                        handleTermChange(termIndex, "rent_price", bruttoToNetto(parsed));
                      }
                      setDraftRentPrice((prev) => {
                        const next = { ...prev };
                        delete next[termIndex];
                        return next;
                      });
                    }
                    handleTermChange(termIndex, "rent_price_input_mode", newMode);
                  }}
                  size="small"
                  sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
                >
                  <ToggleButton value="netto">{t("Net")}</ToggleButton>
                  <ToggleButton value="brutto">{t("Gross")}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            {!contractData.is_brutto &&
              (term.rent_price_input_mode || "netto") === "brutto" &&
              (term.rent_price || (draftRentPrice[termIndex] !== undefined && draftRentPrice[termIndex] !== "")) && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("Calculated as net (VAT 23%)")}: {" "}
                  {formatNumber(
                    draftRentPrice[termIndex]
                      ? (() => {
                          const p = parsePriceInput(draftRentPrice[termIndex]);
                          return Number.isNaN(p) ? term.rent_price : bruttoToNetto(p);
                        })()
                      : term.rent_price
                  )}
                </Typography>
              )}
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
            <CustomAutocomplete
              size="small"
              options={inflationRateChoices}
              idField="value"
              labelField="display_name"
              value={
                inflationRateChoices.find(
                  (c: any) => c.value === contractData.inflation_condition
                ) ?? null
              }
              onChange={(_, newValue) =>
                setContractData((prev) => ({
                  ...prev,
                  inflation_condition: newValue?.value ?? "",
                  inflation_rate_percent: newValue?.value
                    ? prev.inflation_rate_percent
                    : "",
                }))
              }
              getOptionLabel={(option: any) => option.display_name ?? ""}
              isOptionEqualToValue={(option, val) => option?.value === val?.value}
              noOptionsText={t("No options")}
              renderInput={(params) => (
                <TextField {...params} label={t("Inflation condition")} />
              )}
              sx={{ flex: { xs: "1 1 100%", sm: 1 } }}
            />
            <Box sx={{ flex: { xs: "1 1 100%", sm: 1 }, minWidth: 0 }}>
              {contractData.inflation_condition && (
                <TextField
                  size="small"
                  label={t("Inflation rate (%)")}
                  required
                  fullWidth
                  value={contractData.inflation_rate_percent}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (nextValue === "") {
                      setContractData((prev) => ({ ...prev, inflation_rate_percent: "" }));
                      return;
                    }
                    if (!DECIMAL_TWO_PLACES_INPUT_REGEX.test(nextValue)) return;
                    setContractData((prev) => ({
                      ...prev,
                      inflation_rate_percent: nextValue.replace(",", "."),
                    }));
                  }}
                  type="text"
                  inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>{t("Prices")}</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => handleAddPrice(termIndex)}
              disabled={!termHasEquipment(term)}
            >
              {t("Add Price")}
            </Button>
          </Box>

          {term.prices.map((price, priceIndex) => (
            <Box
              key={priceIndex}
              sx={{
                border: "1px dashed #999",
                borderRadius: 1,
                p: 2,
                mb: 1,
                display: "flex",
                gap: 1,
                alignItems: "start",
                backgroundColor: "var(--componentBgColor)",
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flex: 1,
                  flexWrap: { xs: "wrap", md: "nowrap" },
                  minWidth: 0,
                }}
              >
                <FormControl size="small" sx={{ flex: { xs: "1 1 100%", md: 1 } }}>
                  <InputLabel sx={{ color: "var(--textColor)" }}>{t("Kind")}</InputLabel>
                  <Select
                    value={price.kind}
                    onChange={(e) => handlePriceChange(termIndex, priceIndex, "kind", e.target.value)}
                    label={t("Kind")}
                  >
                    {equipmentCounterKindChoices.map((choice: any) => (
                      <MenuItem key={choice.value} value={choice.value}>
                        {choice.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {term.rent_price && (
                  <TextField
                    size="small"
                    label={t("Included in rent")}
                    value={price.included_in_rent ? String(price.included_in_rent).replace(/\./g, ",") : ""}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      if (nextValue === "") {
                        handlePriceChange(termIndex, priceIndex, "included_in_rent", "");
                        return;
                      }
                      if (!DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) return;
                      handlePriceChange(
                        termIndex,
                        priceIndex,
                        "included_in_rent",
                        nextValue.replace(",", ".")
                      );
                    }}
                    sx={{ flex: { xs: "1 1 100%", md: 1 } }}
                    type="text"
                    inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                  />
                )}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    flex: { xs: "1 1 100%", md: 1 },
                  }}
                >
                  {contractData.is_brutto ? (
                    <TextField
                      size="small"
                      label={t("Price (gross)")}
                      value={formatPriceForDisplay(price.price)}
                      onChange={(e) => {
                        const nextValue = e.target.value;
                        if (nextValue === "") {
                          handlePriceChange(termIndex, priceIndex, "price", "");
                          return;
                        }
                        if (!DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) return;
                        handlePriceChange(termIndex, priceIndex, "price", nextValue.replace(",", "."));
                      }}
                      sx={{ flex: { xs: "1 1 100%", md: 1 } }}
                      type="text"
                      inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                    />
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: { xs: "wrap", sm: "nowrap" },
                        }}
                      >
                        <TextField
                          size="small"
                          label={t("Price")}
                          value={
                            (price.price_input_mode || "netto") === "netto"
                              ? formatPriceForDisplay(price.price)
                              : (() => {
                                  const key = `${termIndex}-${priceIndex}`;
                                  const draft = draftPrice[key];
                                  if (draft !== undefined && draft !== "") {
                                    return formatPriceForDisplay(draft);
                                  }
                                  return price.price
                                    ? formatPriceForDisplay(nettoToBrutto(parsePriceInput(price.price)))
                                    : "";
                                })()
                          }
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            const mode = price.price_input_mode || "netto";
                            const key = `${termIndex}-${priceIndex}`;
                            if (nextValue === "") {
                              handlePriceChange(termIndex, priceIndex, "price", "");
                              setDraftPrice((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              });
                              return;
                            }
                            if (!DECIMAL_FOUR_PLACES_INPUT_REGEX.test(nextValue)) return;
                            if (mode === "brutto") {
                              setDraftPrice((prev) => ({ ...prev, [key]: nextValue }));
                            } else {
                              handlePriceChange(termIndex, priceIndex, "price", nextValue.replace(",", "."));
                              setDraftPrice((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              });
                            }
                          }}
                          onBlur={() => {
                            const mode = price.price_input_mode || "netto";
                            const key = `${termIndex}-${priceIndex}`;
                            const draft = draftPrice[key];
                            if (mode === "brutto" && draft !== undefined && draft !== "") {
                              const parsed = parsePriceInput(draft);
                              if (!Number.isNaN(parsed)) {
                                handlePriceChange(termIndex, priceIndex, "price", bruttoToNetto(parsed));
                              }
                              setDraftPrice((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              });
                            }
                          }}
                          sx={{ flex: { xs: "1 1 100%", md: 1 } }}
                          type="text"
                          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
                        />
                        <ToggleButtonGroup
                          value={price.price_input_mode || "netto"}
                          exclusive
                          onChange={(_, newMode: PriceInputMode | null) => {
                            if (!newMode) return;
                            const key = `${termIndex}-${priceIndex}`;
                            const draft = draftPrice[key];
                            if (
                              (price.price_input_mode || "netto") === "brutto" &&
                              draft !== undefined &&
                              draft !== ""
                            ) {
                              const parsed = parsePriceInput(draft);
                              if (!Number.isNaN(parsed)) {
                                handlePriceChange(termIndex, priceIndex, "price", bruttoToNetto(parsed));
                              }
                              setDraftPrice((prev) => {
                                const next = { ...prev };
                                delete next[key];
                                return next;
                              });
                            }
                            handlePriceChange(termIndex, priceIndex, "price_input_mode", newMode);
                          }}
                          size="small"
                          sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}
                        >
                          <ToggleButton value="netto">{t("Net")}</ToggleButton>
                          <ToggleButton value="brutto">{t("Gross")}</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      {(price.price_input_mode || "netto") === "brutto" &&
                        (price.price || (draftPrice[`${termIndex}-${priceIndex}`] !== undefined && draftPrice[`${termIndex}-${priceIndex}`] !== "")) && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {t("Calculated as net (VAT 23%)")}: {" "}
                            {formatNumber(
                              (() => {
                                const key = `${termIndex}-${priceIndex}`;
                                const draft = draftPrice[key];
                                if (draft) {
                                  const p = parsePriceInput(draft);
                                  return Number.isNaN(p) ? price.price : bruttoToNetto(p);
                                }
                                return price.price;
                              })()
                            )}
                          </Typography>
                        )}
                    </>
                  )}
                </Box>
              </Box>

              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemovePrice(termIndex, priceIndex)}
                sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" } }}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
        </Box>

        {hasAmbiguousUsagePricing(term) && (
          <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "var(--textColor)", mb: 1 }}>
              {t("Usage billing")}
            </Typography>
            <ToggleButtonGroup
              value={term.bill_usage_from_start ? "from_start" : "over_rent"}
              exclusive
              onChange={(_, newValue: "over_rent" | "from_start" | null) => {
                if (!newValue) return;
                handleTermChange(termIndex, "bill_usage_from_start", newValue === "from_start");
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="over_rent" sx={{ flex: 1 }}>
                {t("Included in the rent (extra charge only above that amount)")}
              </ToggleButton>
              <ToggleButton value="from_start" sx={{ flex: 1 }}>
                {t("From the first page (rent covers the device only)")}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
              {term.bill_usage_from_start
                ? t("The rent is a flat device fee — every page is billed separately, starting from the first one.")
                : t("Pages are covered by the rent up to its value — you only pay extra once usage goes over it.")}
            </Typography>
          </Box>
        )}

        {termHasNewEquipments(term) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                  {t("Counters")}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddCounter(termIndex)}
                  disabled={!termHasEquipment(term)}
                >
                  {t("Add counter")}
                </Button>
              </Box>

              <CounterDefaultsLoader
                equipmentId={getTermEquipmentIds(term)[0]}
                onChange={(data) =>
                  setLastCountersByTerm((prev) =>
                    prev[termIndex] === data ? prev : { ...prev, [termIndex]: data }
                  )
                }
              />

              {term.counters.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <PreviousCountersTable equipmentId={getTermEquipmentIds(term)[0]} />
                </Box>
              )}

              {term.counters.map((_, counterIndex) => (
                <Box
                  key={counterIndex}
                  sx={{
                    border: "1px dashed #999",
                    borderRadius: 1,
                    p: 2,
                    mb: 1,
                    backgroundColor: "var(--componentBgColor)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                      {t("Counter")} #{counterIndex + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveCounter(termIndex, counterIndex)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <EquipmentCounterForm
                    // Remounts when the term's own equipment changes (single-equipment
                    // terms only - CCR rows pick their equipment per-row via their own
                    // visible field) so the form re-syncs to the new equipment instead
                    // of keeping the previous equipment's stale internal value.
                    key={isCommonCounterTerm(term) ? counterIndex : `${counterIndex}-${getTermEquipmentIds(term)[0] ?? "none"}`}
                    initialData={term.counters[counterIndex] as Record<string, unknown>}
                    equipmentId={
                      !isCommonCounterTerm(term) && getTermEquipmentIds(term).length === 1
                        ? getTermEquipmentIds(term)[0]
                        : undefined
                    }
                    equipmentOptionsOverride={
                      isCommonCounterTerm(term) ? term.equipments || [] : undefined
                    }
                    hiddenFields={isCommonCounterTerm(term) ? ["job"] : ["equipment_id", "job"]}
                    hideSubmit
                    hidePreviousCountersTable
                    onFormDataChange={(data) => handleCounterChange(termIndex, counterIndex, data)}
                  />
                </Box>
              ))}

              {term.counters.length > 0 && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddCounter(termIndex)}
                  disabled={!termHasEquipment(term)}
                >
                  {t("Add counter")}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {isPersistedEditableTerm(term) && term.active === false && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                  {t("End counters")}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddEndCounter(termIndex)}
                  disabled={!termHasEquipment(term)}
                >
                  {t("Add end counter")}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                {t(
                  "Deactivating this term closes out its billing — provide a final reading for each price so its last settlement can be calculated correctly."
                )}
              </Typography>

              <CounterDefaultsLoader
                equipmentId={getTermEquipmentIds(term)[0]}
                onChange={(data) =>
                  setLastCountersByTerm((prev) =>
                    prev[termIndex] === data ? prev : { ...prev, [termIndex]: data }
                  )
                }
              />

              {term.end_counters.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <PreviousCountersTable equipmentId={getTermEquipmentIds(term)[0]} />
                </Box>
              )}

              {term.end_counters.map((_, counterIndex) => (
                <Box
                  key={counterIndex}
                  sx={{
                    border: "1px dashed #999",
                    borderRadius: 1,
                    p: 2,
                    mb: 1,
                    backgroundColor: "var(--componentBgColor)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                      {t("End counter")} #{counterIndex + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveEndCounter(termIndex, counterIndex)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <EquipmentCounterForm
                    initialData={term.end_counters[counterIndex] as Record<string, unknown>}
                    equipmentId={
                      !isCommonCounterTerm(term) && getTermEquipmentIds(term).length === 1
                        ? getTermEquipmentIds(term)[0]
                        : undefined
                    }
                    equipmentOptionsOverride={
                      isCommonCounterTerm(term) ? term.equipments || [] : undefined
                    }
                    hiddenFields={isCommonCounterTerm(term) ? ["job"] : ["equipment_id", "job"]}
                    hideSubmit
                    hidePreviousCountersTable
                    onFormDataChange={(data) => handleEndCounterChange(termIndex, counterIndex, data)}
                  />
                </Box>
              ))}

              {term.end_counters.length > 0 && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleAddEndCounter(termIndex)}
                  disabled={!termHasEquipment(term)}
                >
                  {t("Add end counter")}
                </Button>
              )}

              {term.end_counters.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: term.prices.every((price) => price.has_end_counter)
                      ? "text.secondary"
                      : "error.main",
                  }}
                >
                  {term.prices.every((price) => price.has_end_counter)
                    ? t("All prices already have an end counter.")
                    : t("Add at least one end counter before saving to close out this term.")}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid #ccc",
              borderRadius: "8px !important",
              backgroundColor: "transparent",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                {t("Installation address")} {" "}
                {term.installation_address?.street && (
                  <Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
                    ({[term.installation_address.street, term.installation_address.zip_code, term.installation_address.city].filter(Boolean).join(", ")})
                  </Typography>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {isPersistedEditableTerm(term) && term.active === false && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary", mb: 2 }}
                >
                  {t(
                    "Closing this position clears the installation address of its equipment - the equipment comes back to the company."
                  )}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {termIndex > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleFillFromPreviousPositionAddress(termIndex)}
                    disabled={!contractTerms[termIndex - 1]?.installation_address}
                  >
                    {t("Fill from previous position's address")}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleFillFromLastContractAddress(termIndex, contractData.client_id)}
                  disabled={!contractData.client_id || !!fillLastContractLoadingByTerm[termIndex]}
                  startIcon={
                    fillLastContractLoadingByTerm[termIndex] ? <CircularProgress size={16} /> : undefined
                  }
                >
                  {fillLastContractLoadingByTerm[termIndex]
                    ? t("Loading...")
                    : t("Fill from last contract's address")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleFillFromClientAddress(termIndex, selectedClient)}
                  disabled={!selectedClient?.address || !!fillAddressLoadingByTerm[termIndex]}
                  startIcon={
                    fillAddressLoadingByTerm[termIndex] ? <CircularProgress size={16} /> : undefined
                  }
                >
                  {fillAddressLoadingByTerm[termIndex]
                    ? t("Loading...")
                    : t("Fill from client address")}
                </Button>
              </Box>
              <AddressForm
                value={term.installation_address ?? defaultAddressFormData}
                onChange={(addr) => handleTermChange(termIndex, "installation_address", addr)}
                showCounterReminderEmail
                required={isPersistedEditableTerm(term) && term.active === false ? [] : ["street", "city", "zip_code"]}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    );

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {contractTerms.map((term, termIndex) => {
          const persistedEditableTerm = isPersistedEditableTerm(term);
          const kindChoice = kindChoices.find((choice: any) => choice.value === term.kind);
          const summaryText = getTermSummaryText(term, kindChoice);
          const termKey = getPersistedTermKey(term, termIndex);
          const isTermExpanded = expandedTerms[termKey] ?? true;

          if (persistedEditableTerm) {
            return (
              <Accordion
                key={termKey}
                expanded={isTermExpanded}
                onChange={(_, expanded) => handleTermAccordionChange(termKey, expanded)}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" },
                  backgroundColor: "transparent",
                }}
              >
                <AccordionSummary
                  sx={{
                    px: 2,
                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ color: "var(--textColor)" }}>
                        {`${t("Contract Position")} ${termIndex + 1}`}
                      </Typography>
                      {summaryText ? (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {summaryText}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onFocusCapture={(event) => event.stopPropagation()}
                    >
                      <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                        {t("Active")}
                      </Typography>
                      <Switch
                        edge="end"
                        checked={term.active === true}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(_, checked) => handleTermActiveToggle(termIndex, checked)}
                        inputProps={{ "aria-label": t("Active") }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                  {renderTermBody(term, termIndex)}
                </AccordionDetails>
              </Accordion>
            );
          }

          return (
            <Box
              key={termKey}
              sx={{
                border: "1px solid #ccc",
                borderRadius: 2,
                p: 2,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: "var(--textColor)" }}>
                  {`${t("Contract Position")} ${termIndex + 1}`}
                </Typography>
                {contractTerms.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveTerm(termIndex)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>
              {renderTermBody(term, termIndex)}
            </Box>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddTerm}
          sx={{ alignSelf: "flex-start" }}
        >
          {t("Add Equipment")}
        </Button>
      </Box>
    );
  };

  const renderReviewStep = () => {
    const selectedClient = clients.find((c) => c.id === contractData.client_id);
    const selectedSchema = numerationSchemas.find(
      (s) => s.id === contractData.numeration_schema_id
    );
    const inflationChoice =
      formOptions.contract?.inflation_condition?.choices?.find(
        (c: any) => c.value === contractData.inflation_condition
      );
    const counterKindDisplayName = (kind: string) => {
      const labels: Record<string, string> = {
        BP: t("Black pages"),
        CP: t("Color pages"),
        SP: t("Scanned pages"),
      };
      return kind ? labels[kind] || kind : "-";
    };

    const hasNoActiveTerms =
      contractTerms.length === 0 || contractTerms.every((term) => term.active === false);
    const endDateIsOpenEnded =
      !contractData.end_date ||
      contractData.indefinite_after_end_date ||
      dayjs(contractData.end_date).isAfter(dayjs(), "day");
    const showCloseContractPrompt =
      hasNoActiveTerms && endDateIsOpenEnded && !closeContractPromptDismissed;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {showCloseContractPrompt && (
          <Alert
            severity="warning"
            onClose={() => setCloseContractPromptDismissed(true)}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setContractData((prev) => ({
                    ...prev,
                    end_date: dayjs().format("YYYY-MM-DD"),
                  }));
                  setCloseContractPromptDismissed(true);
                }}
              >
                {t("Close it from today")}
              </Button>
            }
          >
            {t("This contract has no assigned equipments, do you want to close it from today?")}
          </Alert>
        )}

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: "var(--textColor)" }}>
            {t("Contract Information")}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {contractData.internal_name && (
              <Typography sx={{ color: "var(--textColor)" }}>
                <strong>{t("Internal name")}:</strong> {contractData.internal_name}
              </Typography>
            )}
            <Typography sx={{ color: "var(--textColor)" }}>
              <strong>{t("Client")}:</strong> {selectedClient?.name || "-"}
            </Typography>
            <Typography sx={{ color: "var(--textColor)" }}>
              <strong>{t("Numeration schema")}:</strong>{" "}
              {selectedSchema?.name || "-"}
            </Typography>
            <Typography sx={{ color: "var(--textColor)" }}>
              <strong>{t("Number")}:</strong> {contractData.number || "-"}
            </Typography>
            <Typography sx={{ color: "var(--textColor)" }}>
              <strong>{t("Start date")}:</strong> {contractData.start_date || "-"}
            </Typography>
            <Typography sx={{ color: "var(--textColor)" }}>
              <strong>{t("End date")}:</strong>{" "}
              <ContractEndDateDisplay
                endDate={contractData.end_date}
                indefiniteAfterEndDate={contractData.indefinite_after_end_date}
                t={t}
              />
            </Typography>
            {contractData.issuance_date && (
              <Typography sx={{ color: "var(--textColor)" }}>
                <strong>{t("Issuance date")}:</strong> {contractData.issuance_date}
              </Typography>
            )}
            {contractData.signature_date && (
              <Typography sx={{ color: "var(--textColor)" }}>
                <strong>{t("Signature date")}:</strong>{" "}
                {contractData.signature_date}
              </Typography>
            )}
            {contractData.inflation_condition ? (
              <>
                <Typography sx={{ color: "var(--textColor)" }}>
                  <strong>{t("Inflation condition")}:</strong>{" "}
                  {inflationChoice?.display_name ?? contractData.inflation_condition}
                </Typography>
                {contractData.inflation_rate_percent && (
                  <Typography sx={{ color: "var(--textColor)" }}>
                    <strong>{t("Inflation rate")}:</strong>{" "}
                    {contractData.inflation_rate_percent}%
                  </Typography>
                )}
              </>
            ) : null}
            <Typography sx={{ color: "var(--textColor)" }}>
              <strong>{t("Values stored as")}:</strong>{" "}
              {contractData.is_brutto ? t("Gross (Brutto)") : t("Net (Netto)")}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: "var(--textColor)" }}>
            {t("Contract Positions")} ({contractTerms.length})
          </Typography>
          {contractTerms.map((term, index) => {
            const kindChoice = formOptions.term?.kind?.choices?.find(
              (c: any) => c.value === term.kind
            );
            const cycleChoice = formOptions.term?.settlement_cycle?.choices?.find(
              (c: any) => c.value === term.settlement_cycle
            );

            return (
              <Box
                key={index}
                sx={{ border: "1px solid #eee", borderRadius: 1, p: 2, mb: 2 }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ color: "var(--textColor)" }}>
                  {t("Contract Position")} {index + 1}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                    <strong>
                      {isCommonCounterTerm(term) ? t("Equipments") : t("Equipment")}:
                    </strong>{" "}
                    {getTermEquipmentLabel(term)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                    <strong>{t("Kind")}:</strong>{" "}
                    {kindChoice?.display_name || term.kind}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                    <strong>{t("Settlement cycle")}:</strong>{" "}
                    {cycleChoice?.display_name || term.settlement_cycle}
                  </Typography>
                  {isPersistedEditableTerm(term) ? (
                    <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                      <strong>{t("Active")}:</strong> {term.active ? t("Yes") : t("No")}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                    <strong>{t("Next settlement date")}:</strong>{" "}
                    {term.next_settlement_date ? dayjs(term.next_settlement_date).format("YYYY-MM-DD") : "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                    <strong>{t("Rent price")}:</strong> {formatNumber(term.rent_price)}{" "}
                    ({contractData.is_brutto ? t("Gross") : t("Net")})
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                    <strong>{t("Settlement kind")}:</strong> {computeSettlementKindLabel(term, t)}
                  </Typography>
                  {term.installation_address?.street && (
                    <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                      <strong>{t("Installation address")}:</strong>{" "}
                      {[term.installation_address.street, term.installation_address.zip_code, term.installation_address.city, term.installation_address.country].filter(Boolean).join(", ")}
                    </Typography>
                  )}
                  {term.prices.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "var(--textColor)", fontWeight: 600, mb: 0.5 }}
                      >
                        {t("Prices")}
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.25,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        {term.prices.map((price, priceIdx) => {
                          const mode = price.price_input_mode || "netto";
                          const parsedNet =
                            price.price !== "" && price.price != null
                              ? parsePriceInput(String(price.price))
                              : NaN;
                          const hasValidNet = !Number.isNaN(parsedNet);
                          let priceSummary = "-";
                          if (hasValidNet) {
                            if (contractData.is_brutto) {
                              priceSummary = `${formatNumber(parsedNet)} (${t("Gross")})`;
                            } else if (mode === "brutto") {
                              const grossValue = parsePriceInput(nettoToBrutto(parsedNet));
                              priceSummary = `${formatNumber(grossValue)} (${t("Gross")})`;
                            } else {
                              priceSummary = `${formatNumber(parsedNet)} (${t("Net")})`;
                            }
                          }
                          return (
                            <Box
                              component="li"
                              key={priceIdx}
                              sx={{
                                color: "var(--textColor)",
                                "&::marker": { color: "var(--textColor)" },
                              }}
                            >
                              <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                                <strong>{counterKindDisplayName(price.kind)}</strong>
                                {" — "}
                                <strong>{t("Price")}:</strong> {priceSummary}
                                {term.rent_price && computeSettlementKindLabel(term, t) === t("Rent with usage limit") ? (
                                  <>
                                    {" · "}
                                    <strong>{t("Included in rent")}:</strong>{" "}
                                    {(() => {
                                      const raw = price.included_in_rent as unknown;
                                      if (raw === "" || raw === null || raw === undefined) {
                                        return "-";
                                      }
                                      const n = Number(raw);
                                      return Number.isNaN(n) ? "-" : formatNumber(n);
                                    })()}
                                  </>
                                ) : null}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {term.counters.some(
                    (counter) =>
                      counter.value != null && counter.value !== "" && Boolean(counter.kind)
                  ) && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "var(--textColor)", fontWeight: 600, mb: 0.5 }}
                      >
                        {t("Counters")}
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.25,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        {term.counters
                          .filter(
                            (counter) =>
                              counter.value != null &&
                              counter.value !== "" &&
                              Boolean(counter.kind)
                          )
                          .map((counter, counterIdx) => (
                            <Box
                              component="li"
                              key={counterIdx}
                              sx={{
                                color: "var(--textColor)",
                                "&::marker": { color: "var(--textColor)" },
                              }}
                            >
                              <Typography variant="body2" sx={{ color: "var(--textColor)" }}>
                                {isCommonCounterTerm(term) ? (
                                  <>
                                    <strong>{t("Equipment")}:</strong>{" "}
                                    {getTermEquipmentLabelById(term, counter.equipment_id)}
                                    {" · "}
                                  </>
                                ) : null}
                                <strong>{counterKindDisplayName(String(counter.kind))}</strong>
                                {" — "}
                                <strong>{t("Value")}:</strong> {formatCounterValue(String(counter.value))}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  if (!formOptions.contract || !formOptions.term) {
    return <CustomCircularProgress />;
  }

  return (
    <Box sx={{ width: "100%", minHeight: 400 }}>
      <Stepper activeStep={activeStep} alternativeLabel={isSmallScreen} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4, minHeight: 300 }}>
        {activeStep === 0 && renderContractStep()}
        {activeStep === 1 && renderContractTermsStep()}
        {activeStep === 2 && renderReviewStep()}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
          {t("Back")}
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? t("Creating...") : t("Submit")}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              {t("Next")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
